/**
 * Clase para manejar impresoras Bluetooth térmicas
 * Compatible con impresoras ESC/POS
 */

class BluetoothPrinter {
  constructor() {
    this.device = null;
    this.characteristic = null;
    this.encoder = new TextEncoder();
  }

  /**
   * Verifica si el navegador soporta Web Bluetooth API
   */
  isBluetoothAvailable() {
    return 'bluetooth' in navigator;
  }

  /**
   * Conecta con una impresora Bluetooth
   */
  async connect() {
    if (!this.isBluetoothAvailable()) {
      throw new Error('Web Bluetooth API no está disponible en este navegador');
    }

    try {
      // Solicitar dispositivo Bluetooth
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: ['000018f0-0000-1000-8000-00805f9b34fb'] }, // Servicio común de impresoras
        ],
        optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
      });

      // Conectar al servidor GATT
      const server = await this.device.gatt.connect();
      
      // Obtener el servicio
      const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
      
      // Obtener la característica de escritura
      this.characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');
      
      return true;
    } catch (error) {
      console.error('Error al conectar con la impresora:', error);
      throw new Error('No se pudo conectar con la impresora Bluetooth');
    }
  }

  /**
   * Verifica si está conectado
   */
  isConnected() {
    return this.device && this.device.gatt.connected;
  }

  /**
   * Desconecta de la impresora
   */
  disconnect() {
    if (this.device && this.device.gatt.connected) {
      this.device.gatt.disconnect();
    }
  }

  /**
   * Comandos ESC/POS
   */
  get ESC_POS() {
    return {
      INIT: [0x1B, 0x40], // Inicializar impresora
      ALIGN_LEFT: [0x1B, 0x61, 0x00],
      ALIGN_CENTER: [0x1B, 0x61, 0x01],
      ALIGN_RIGHT: [0x1B, 0x61, 0x02],
      BOLD_ON: [0x1B, 0x45, 0x01],
      BOLD_OFF: [0x1B, 0x45, 0x00],
      SIZE_NORMAL: [0x1D, 0x21, 0x00],
      SIZE_DOUBLE: [0x1D, 0x21, 0x11],
      SIZE_LARGE: [0x1D, 0x21, 0x22],
      FEED_LINE: [0x0A],
      CUT_PAPER: [0x1D, 0x56, 0x00],
    };
  }

  /**
   * Envía datos a la impresora
   */
  async sendData(data) {
    if (!this.isConnected()) {
      throw new Error('No hay conexión con la impresora');
    }

    try {
      const buffer = new Uint8Array(data);
      await this.characteristic.writeValue(buffer);
    } catch (error) {
      console.error('Error al enviar datos:', error);
      throw error;
    }
  }

  /**
   * Envía texto a la impresora
   */
  async sendText(text) {
    const encoded = this.encoder.encode(text);
    await this.sendData(encoded);
  }

  /**
   * Imprime el recibo de entrega
   */
  async printEntregaReceipt(entrega) {
    if (!this.isConnected()) {
      await this.connect();
    }

    // Un comprobante de entrega solo aplica cuando ya fue entregado.
    // Mientras esté en otro estado se imprime un ticket de alistamiento.
    if (entrega.estado !== 'entregado') {
      return this.printAlistamientoReceipt(entrega);
    }

    try {
      // Inicializar
      await this.sendData(this.ESC_POS.INIT);
      
      // Header
      await this.sendData(this.ESC_POS.ALIGN_CENTER);
      await this.sendData(this.ESC_POS.SIZE_DOUBLE);
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText('HOUSY ERP\n');
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendData(this.ESC_POS.SIZE_NORMAL);
      await this.sendText('Sistema de Entregas\n');
      await this.sendText('================================\n\n');
      
      // Tipo de comprobante
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText('COMPROBANTE DE ENTREGA\n\n');
      await this.sendData(this.ESC_POS.BOLD_OFF);
      
      // Consecutivo
      if (entrega.consecutivo) {
        await this.sendText(`Consecutivo: ${entrega.consecutivo}\n\n`);
      }
      
      // Información de la entrega
      await this.sendData(this.ESC_POS.ALIGN_LEFT);
      await this.sendText('--------------------------------\n');
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText('DATOS DE LA ENTREGA\n');
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendText('--------------------------------\n');
      
      const fecha = new Date(entrega.fechaEntrega || new Date()).toLocaleString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      await this.sendText(`Fecha: ${fecha}\n`);
      
      const tipo = entrega.tipo || 'insumos';
      await this.sendText(`Tipo: ${tipo === 'lenceria' ? 'Lenceria' : 'Insumos'}\n`);
      await this.sendText(`Edificio: ${entrega.edificio?.nombre || 'N/A'}\n`);
      
      if (entrega.hospedaje?.nombre) {
        await this.sendText(`Activo: ${entrega.hospedaje.nombre}\n`);
      }
      
      if (entrega.solicitante || entrega.creator?.nombre) {
        await this.sendText(`Solicitante: ${entrega.solicitante || entrega.creator?.nombre}\n`);
      }
      
      await this.sendText('\n');
      
      // Items
      await this.sendText('--------------------------------\n');
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText(`${tipo === 'lenceria' ? 'PRENDAS' : 'INSUMOS'} ENTREGADOS\n`);
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendText('--------------------------------\n');
      
      const items = entrega.items || [];
      const totalItems = items.reduce((sum, item) => sum + (item.cantidad || 0), 0);
      
      if (items.length > 0) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          await this.sendText(`${i + 1}. ${item.nombre}\n`);
          await this.sendText(`   Cantidad: ${item.cantidad || 1}\n`);
          if (tipo === 'lenceria' && item.peso) {
            await this.sendText(`   Peso: ${item.peso} g\n`);
          }
          if (item.descripcion) {
            await this.sendText(`   Desc: ${item.descripcion}\n`);
          }
          if (item.observacion || item.observaciones) {
            await this.sendText(`   Obs: ${item.observacion || item.observaciones}\n`);
          }
          await this.sendText('\n');
        }
      } else {
        await this.sendText('No hay items registrados\n\n');
      }
      
      // Total
      await this.sendText('================================\n');
      await this.sendData(this.ESC_POS.ALIGN_CENTER);
      await this.sendData(this.ESC_POS.SIZE_DOUBLE);
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText(`TOTAL: ${totalItems}\n`);
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendData(this.ESC_POS.SIZE_NORMAL);
      await this.sendText('================================\n\n');
      
      // Firma
      await this.sendData(this.ESC_POS.ALIGN_LEFT);
      await this.sendText('_________________________\n');
      await this.sendText('   Firma del Receptor\n\n');
      await this.sendText('Confirmo haber recibido los\n');
      await this.sendText('items descritos en este\n');
      await this.sendText('comprobante\n\n');
      
      // Footer
      await this.sendText('--------------------------------\n');
      if (entrega.entregadoPor?.nombre) {
        await this.sendText(`Entregado por: ${entrega.entregadoPor.nombre}\n`);
      }
      await this.sendText('\n');
      await this.sendData(this.ESC_POS.ALIGN_CENTER);
      await this.sendText('Gracias por su confianza\n');
      await this.sendText('================================\n\n\n\n');
      
      // Cortar papel
      await this.sendData(this.ESC_POS.CUT_PAPER);
      
    } catch (error) {
      console.error('Error al imprimir recibo:', error);
      throw error;
    }
  }

  /**
   * Imprime un ticket de alistamiento (packing slip) vía Bluetooth.
   * Muestra los datos del alistamiento y las prendas, sin firma ni receptor.
   */
  async printAlistamientoReceipt(entrega) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      // Inicializar
      await this.sendData(this.ESC_POS.INIT);

      // Header
      await this.sendData(this.ESC_POS.ALIGN_CENTER);
      await this.sendData(this.ESC_POS.SIZE_DOUBLE);
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText('HOUSY\n');
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendData(this.ESC_POS.SIZE_NORMAL);
        // Título
      await this.sendText('--------------------------------\n');
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText('ALISTAMIENTO\n');
      await this.sendData(this.ESC_POS.BOLD_OFF);

      // Datos: solo edificio, fecha y hora
      await this.sendData(this.ESC_POS.ALIGN_LEFT);
      await this.sendText('--------------------------------\n');

      const fecha = new Date(
        entrega.fecha_alistamiento || entrega.createdAt || new Date()
      ).toLocaleString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      await this.sendText(`Edificio: ${entrega.edificio?.nombre || 'N/A'}\n`);
      // Activo (solo aplica a logística horizontal)
      const activoNombre = entrega.hospedaje?.nombre || entrega.hospedaje?.nombrePropiedad;
      if (activoNombre) {
        await this.sendText(`Activo: ${activoNombre}\n`);
      }
      await this.sendText(`Fecha: ${fecha}\n`);
      // Observación solo si existe
      if (entrega.observaciones) {
        await this.sendText(`Observaciones: ${entrega.observaciones}\n`);
      }
      // Items alistados
      const tipo = entrega.tipo || 'lenceria';
      await this.sendText('--------------------------------\n');
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText(`ITEMS\n`);
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendText('--------------------------------\n');

      const items = entrega.items || [];
      const totalItems = items.reduce((sum, item) => sum + (item.cantidad || 0), 0);

      if (items.length > 0) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          await this.sendText(`${i + 1}. ${item.nombre}\n`);
          await this.sendText(`   Cantidad: ${item.cantidad || 1}\n`);
          await this.sendText('\n');
        }
      } else {
        await this.sendText('No hay items registrados\n\n');
      }

      // Total
      await this.sendText('================================\n');
      await this.sendData(this.ESC_POS.ALIGN_CENTER);
      await this.sendData(this.ESC_POS.SIZE_DOUBLE);
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText(`TOTAL: ${totalItems}\n`);
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendData(this.ESC_POS.SIZE_NORMAL);
      await this.sendText('================================\n\n\n\n');

      // Cortar papel
      await this.sendData(this.ESC_POS.CUT_PAPER);

    } catch (error) {
      console.error('Error al imprimir ticket de alistamiento:', error);
      throw error;
    }
  }

  /**
   * Imprime el ticket de entrega en DOS copias (Lavandería y Cliente),
   * cada una con espacios para firmar. Pensado para el cierre de la entrega.
   */
  async printEntregaDobleCopia(entrega) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      await this._printEntregaCopiaFirma(entrega, 'LAVANDERIA');
      await this._printEntregaCopiaFirma(entrega, 'CLIENTE');
    } catch (error) {
      console.error('Error al imprimir doble copia de entrega:', error);
      throw error;
    }
  }

  /**
   * Imprime UNA copia del comprobante de entrega con espacios para firmar.
   */
  async _printEntregaCopiaFirma(entrega, copiaLabel) {
    // Inicializar
    await this.sendData(this.ESC_POS.INIT);

    // Header
    await this.sendData(this.ESC_POS.ALIGN_CENTER);
    await this.sendData(this.ESC_POS.SIZE_DOUBLE);
    await this.sendData(this.ESC_POS.BOLD_ON);
    await this.sendText('HOUSY\n');
    await this.sendData(this.ESC_POS.BOLD_OFF);
    await this.sendData(this.ESC_POS.SIZE_NORMAL);
    await this.sendText('COMPROBANTE DE ENTREGA\n');
    await this.sendData(this.ESC_POS.BOLD_ON);
    await this.sendText(`COPIA: ${copiaLabel}\n`);
    await this.sendData(this.ESC_POS.BOLD_OFF);
    await this.sendText('================================\n');

    // Datos
    await this.sendData(this.ESC_POS.ALIGN_LEFT);
    const consecutivo = entrega.consecutivo || entrega.solicitud_id;
    if (consecutivo) {
      await this.sendText(`Consecutivo: ${consecutivo}\n`);
    }
    const fecha = new Date(
      entrega.fechaEntrega || entrega.fecha_entrega || new Date()
    ).toLocaleString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    await this.sendText(`Fecha: ${fecha}\n`);
    await this.sendText(`Edificio: ${entrega.edificio?.nombre || 'N/A'}\n`);
    // Activo (solo aplica a logística horizontal)
    const activo = entrega.hospedaje?.nombre || entrega.hospedaje?.nombrePropiedad;
    if (activo) {
      await this.sendText(`Activo: ${activo}\n`);
    }
    const receptor = entrega.receptor_nombre || entrega.receptorNombre;
    if (receptor) {
      await this.sendText(`Recibe: ${receptor}\n`);
    }
    await this.sendText('--------------------------------\n');

    // Items
    await this.sendData(this.ESC_POS.BOLD_ON);
    await this.sendText('ITEMS ENTREGADOS\n');
    await this.sendData(this.ESC_POS.BOLD_OFF);
    await this.sendText('--------------------------------\n');

    const tipo = entrega.tipo || 'lenceria';
    const items = entrega.items || [];
    const totalItems = items.reduce((sum, item) => sum + (item.cantidad || 0), 0);

    if (items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        await this.sendText(`${i + 1}. ${item.nombre}\n`);
        await this.sendText(`   Cantidad: ${item.cantidad || 1}\n`);
        if (tipo === 'lenceria' && item.peso) {
          await this.sendText(`   Peso: ${item.peso} g\n`);
        }
        await this.sendText('\n');
      }
    } else {
      await this.sendText('No hay items registrados\n\n');
    }

    // Total
    await this.sendText('================================\n');
    await this.sendData(this.ESC_POS.ALIGN_CENTER);
    await this.sendData(this.ESC_POS.BOLD_ON);
    await this.sendText(`TOTAL ITEMS: ${totalItems}\n`);
    // Peso real total (o estimado como respaldo)
    const pesoGramos = entrega.peso_real != null ? entrega.peso_real : entrega.peso_total;
    if (pesoGramos) {
      await this.sendText(`PESO TOTAL: ${(pesoGramos / 1000).toFixed(2)} kg\n`);
    }
    await this.sendData(this.ESC_POS.BOLD_OFF);
    await this.sendText('================================\n\n');

    // Espacios para firmar
    await this.sendData(this.ESC_POS.ALIGN_LEFT);
    await this.sendText('\n');
    await this.sendText('_________________________\n');
    await this.sendText('Firma quien entrega\n');
    const entregadoPor = entrega.entregadoPor?.nombre || entrega.conductor?.nombre;
    if (entregadoPor) {
      await this.sendText(`${entregadoPor}\n`);
    }
    await this.sendText('\n\n');
    await this.sendText('_________________________\n');
    await this.sendText('Firma quien recibe\n');
    if (receptor) {
      await this.sendText(`${receptor}\n`);
    }
    await this.sendData(this.ESC_POS.ALIGN_CENTER);
    await this.sendText('\n================================\n\n\n\n');

    // Cortar papel
    await this.sendData(this.ESC_POS.CUT_PAPER);
  }

  /**
   * Imprime el ticket de recolección en DOS copias (Lavandería y Cliente),
   * cada una con espacios para firmar. Mismo formato que la entrega.
   */
  async printRecoleccionDobleCopia(recoleccion) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      await this._printRecoleccionCopiaFirma(recoleccion, 'LAVANDERIA');
      await this._printRecoleccionCopiaFirma(recoleccion, 'CLIENTE');
    } catch (error) {
      console.error('Error al imprimir doble copia de recolección:', error);
      throw error;
    }
  }

  /**
   * Imprime UNA copia del comprobante de recolección con espacios para firmar.
   */
  async _printRecoleccionCopiaFirma(recoleccion, copiaLabel) {
    // Inicializar
    await this.sendData(this.ESC_POS.INIT);

    // Header
    await this.sendData(this.ESC_POS.ALIGN_CENTER);
    await this.sendData(this.ESC_POS.SIZE_DOUBLE);
    await this.sendData(this.ESC_POS.BOLD_ON);
    await this.sendText('HOUSY\n');
    await this.sendData(this.ESC_POS.BOLD_OFF);
    await this.sendData(this.ESC_POS.SIZE_NORMAL);
    await this.sendText('COMPROBANTE DE RECOLECCION\n');
    await this.sendData(this.ESC_POS.BOLD_ON);
    await this.sendText(`COPIA: ${copiaLabel}\n`);
    await this.sendData(this.ESC_POS.BOLD_OFF);
    await this.sendText('================================\n');

    // Datos
    await this.sendData(this.ESC_POS.ALIGN_LEFT);
    const consecutivo = recoleccion.consecutivo || recoleccion.consecutivo_recoleccion;
    if (consecutivo) {
      await this.sendText(`Consecutivo: ${consecutivo}\n`);
    }
    const fecha = new Date(
      recoleccion.fecha || recoleccion.fecha_entrega || new Date()
    ).toLocaleString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    await this.sendText(`Fecha: ${fecha}\n`);
    await this.sendText(`Edificio: ${recoleccion.edificio?.nombre || 'N/A'}\n`);
    if (recoleccion.hospedaje?.nombre || recoleccion.hospedaje?.nombrePropiedad) {
      await this.sendText(`Activo: ${recoleccion.hospedaje.nombre || recoleccion.hospedaje.nombrePropiedad}\n`);
    }
    const entregaNombre = recoleccion.receptor_nombre;
    if (entregaNombre) {
      await this.sendText(`Entrega: ${entregaNombre}\n`);
    }
    await this.sendText('--------------------------------\n');

    // Items
    await this.sendData(this.ESC_POS.BOLD_ON);
    await this.sendText('PRENDAS RECOLECTADAS\n');
    await this.sendData(this.ESC_POS.BOLD_OFF);
    await this.sendText('--------------------------------\n');

    const items = recoleccion.items || recoleccion.prendas || [];
    const totalItems = items.reduce((sum, item) => sum + (item.cantidad || 0), 0);

    if (items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        await this.sendText(`${i + 1}. ${item.nombre}\n`);
        await this.sendText(`   Cantidad: ${item.cantidad || 1}\n`);
        if (item.peso) {
          await this.sendText(`   Peso: ${item.peso} g\n`);
        }
        await this.sendText('\n');
      }
    } else {
      await this.sendText('No hay prendas registradas\n\n');
    }

    // Total
    await this.sendText('================================\n');
    await this.sendData(this.ESC_POS.ALIGN_CENTER);
    await this.sendData(this.ESC_POS.BOLD_ON);
    await this.sendText(`TOTAL PRENDAS: ${totalItems}\n`);
    // Peso real total (o estimado como respaldo)
    const pesoGramos = recoleccion.peso_real != null ? recoleccion.peso_real : recoleccion.peso_total;
    if (pesoGramos) {
      await this.sendText(`PESO TOTAL: ${(pesoGramos / 1000).toFixed(2)} kg\n`);
    }
    await this.sendData(this.ESC_POS.BOLD_OFF);
    await this.sendText('================================\n\n');

    // Espacios para firmar
    await this.sendData(this.ESC_POS.ALIGN_LEFT);
    await this.sendText('\n');
    await this.sendText('_________________________\n');
    await this.sendText('Firma quien entrega\n');
    if (entregaNombre) {
      await this.sendText(`${entregaNombre}\n`);
    }
    await this.sendText('\n\n');
    await this.sendText('_________________________\n');
    await this.sendText('Firma quien recibe\n');
    const conductorNombre = recoleccion.conductor?.nombre;
    if (conductorNombre) {
      await this.sendText(`${conductorNombre}\n`);
    }
    await this.sendData(this.ESC_POS.ALIGN_CENTER);
    await this.sendText('\n================================\n\n\n\n');

    // Cortar papel
    await this.sendData(this.ESC_POS.CUT_PAPER);
  }

  /**
   * Imprime el resumen del proceso de desmanche
   */
  async printDesmancheReceipt(desmanche) {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      // Calcular días
      const calcularDias = () => {
        if (desmanche.estado === 'Finalizado' && desmanche.dias) {
          return desmanche.dias;
        }
        const fechaRegistro = new Date(desmanche.fechaRegistro);
        const fechaActual = new Date();
        const diferenciaMilisegundos = fechaActual.getTime() - fechaRegistro.getTime();
        return Math.ceil(diferenciaMilisegundos / (1000 * 60 * 60 * 24));
      };

      // Inicializar
      await this.sendData(this.ESC_POS.INIT);
      
      // Header
      await this.sendData(this.ESC_POS.ALIGN_CENTER);
      await this.sendData(this.ESC_POS.SIZE_DOUBLE);
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText('HOUSY ERP\n');
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendData(this.ESC_POS.SIZE_NORMAL);
      await this.sendText('Sistema de Lavanderia\n');
      await this.sendText('================================\n\n');
      
      // Tipo de comprobante
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText('PROCESO DE DESMANCHE\n\n');
      await this.sendData(this.ESC_POS.BOLD_OFF);
      
      // Estado
      await this.sendData(this.ESC_POS.ALIGN_CENTER);
      await this.sendData(this.ESC_POS.SIZE_LARGE);
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText(`${desmanche.estado}\n\n`);
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendData(this.ESC_POS.SIZE_NORMAL);
      
      // Información del edificio
      await this.sendData(this.ESC_POS.ALIGN_LEFT);
      await this.sendText('--------------------------------\n');
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText('EDIFICIO\n');
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendText('--------------------------------\n');
      await this.sendText(`${desmanche.edificio?.nombre || 'N/A'}\n`);
      if (desmanche.edificio?.ciudad) {
        await this.sendText(`Ciudad: ${desmanche.edificio.ciudad}\n`);
      }
      await this.sendText('\n');
      
      // Información del proceso
      await this.sendText('--------------------------------\n');
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText('INFORMACION DEL PROCESO\n');
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendText('--------------------------------\n');
      
      await this.sendText(`Tipo de Mancha: ${desmanche.tipoMancha}\n`);
      await this.sendText(`Responsable: ${desmanche.responsable}\n`);
      await this.sendText(`Total Prendas: ${desmanche.totalPrendas}\n`);
      
      const fechaRegistro = new Date(desmanche.fechaRegistro).toLocaleString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      await this.sendText(`Fecha Registro: ${fechaRegistro}\n`);
      
      const dias = calcularDias();
      await this.sendText(`Dias en Proceso: ${dias} ${dias === 1 ? 'dia' : 'dias'}\n`);
      await this.sendText('\n');
      
      // Detalle de prendas
      await this.sendText('--------------------------------\n');
      await this.sendData(this.ESC_POS.BOLD_ON);
      await this.sendText('DETALLE DE PRENDAS\n');
      await this.sendData(this.ESC_POS.BOLD_OFF);
      await this.sendText('--------------------------------\n');
      
      const prendas = desmanche.prendas || [];
      if (prendas.length > 0) {
        for (let i = 0; i < prendas.length; i++) {
          const prenda = prendas[i];
          await this.sendText(`${i + 1}. ${prenda.nombre}\n`);
          await this.sendText(`   Cantidad: ${prenda.cantidad}\n`);
          if (prenda.peso) {
            await this.sendText(`   Peso: ${prenda.peso} g\n`);
          }
          await this.sendText('\n');
        }
      }
      
      // Resultados (si está finalizado)
      if (desmanche.estado === 'Finalizado') {
        await this.sendText('================================\n');
        await this.sendData(this.ESC_POS.ALIGN_CENTER);
        await this.sendData(this.ESC_POS.BOLD_ON);
        await this.sendText('RESULTADOS\n');
        await this.sendData(this.ESC_POS.BOLD_OFF);
        await this.sendText('================================\n\n');
        
        await this.sendData(this.ESC_POS.ALIGN_LEFT);
        await this.sendData(this.ESC_POS.SIZE_DOUBLE);
        await this.sendText(`Recuperadas: ${desmanche.prendasRecuperadas || 0}\n`);
        await this.sendText(`No Recuperadas: ${desmanche.prendasNoRecuperadas || 0}\n`);
        await this.sendData(this.ESC_POS.SIZE_NORMAL);
        
        const porcentaje = Math.round((desmanche.prendasRecuperadas / desmanche.totalPrendas) * 100);
        await this.sendText(`\nExito: ${porcentaje}%\n\n`);
        
        if (desmanche.fechaFinalizacion) {
          const fechaFin = new Date(desmanche.fechaFinalizacion).toLocaleString('es-CO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
          await this.sendText(`Finalizado: ${fechaFin}\n\n`);
        }
      }
      
      // Observaciones
      if (desmanche.observaciones) {
        await this.sendData(this.ESC_POS.ALIGN_LEFT);
        await this.sendText('--------------------------------\n');
        await this.sendData(this.ESC_POS.BOLD_ON);
        await this.sendText('OBSERVACIONES\n');
        await this.sendData(this.ESC_POS.BOLD_OFF);
        await this.sendText('--------------------------------\n');
        await this.sendText(`${desmanche.observaciones}\n\n`);
      }
      
      // Footer
      await this.sendText('--------------------------------\n');
      await this.sendText(`Registrado por:\n${desmanche.creator?.nombre || 'N/A'}\n\n`);
      
      await this.sendData(this.ESC_POS.ALIGN_CENTER);
      await this.sendText('Gracias por su confianza\n');
      await this.sendText('================================\n\n\n\n');
      
      // Cortar papel
      await this.sendData(this.ESC_POS.CUT_PAPER);
      
    } catch (error) {
      console.error('Error al imprimir resumen de desmanche:', error);
      throw error;
    }
  }
}

// Instancia singleton
let printerInstance = null;

/**
 * Obtiene la instancia singleton de la impresora
 */
export const getPrinterInstance = () => {
  if (!printerInstance) {
    printerInstance = new BluetoothPrinter();
  }
  return printerInstance;
};

export default BluetoothPrinter;
